import {Button, Grid, InputLabel, Stack} from '@mui/material';
import {AppButton} from 'components/buttons';
import {AppDialog} from 'components/dialogs';
import {AppControlledAutoComplete} from 'components/form';
import {SingleFileUpload} from 'components/form/dropzone';
import {SelectOptionsType} from 'global';
import {DocumentNode} from 'graphql';
import {useBoardRedux} from 'hooks';
import {ItemStatus} from 'models/board';
import React, {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {CustomFile} from 'types';

interface AppUploadExcelCsvProps {
    isLoading: boolean;
    title: string;
    open: boolean;
    onClose: () => void;
    onUpload: (file: CustomFile, selectedStatus: string, selectedField: string) => void;
    onFinalUpload?: () => void;
    uploadMutation?: DocumentNode;
    itemStatues: ItemStatus[];
}

export const AppUploadExcelCsv: React.FC<AppUploadExcelCsvProps> = ({
    title,
    open,
    isLoading,
    onClose,
    onUpload,
    itemStatues,
    onFinalUpload,
}) => {
    const [fieldsTitles, setFieldTitles] = useState<{value: string; label: string; key?: string | undefined}[]>([]);
    const {selectedBoard, selectedDefaultForm} = useBoardRedux();
    useEffect(() => {
        if (selectedBoard) {
            setFieldTitles(prev => {
                const newFields = selectedDefaultForm.formFieldsData.map(field => ({
                    value: field.title,
                    label: field.title,
                    key: field.title,
                }));
                if (JSON.stringify(prev) !== JSON.stringify(newFields)) {
                    return newFields;
                }
                return prev;
            });
        }
    }, [selectedBoard]);
    const statusOptions = useMemo(() => {
        return itemStatues.map(
            status =>
                ({
                    value: status.id,
                    label: status.title,
                }) as SelectOptionsType,
        );
    }, [itemStatues]);

    const {
        setValue,
        control,
        handleSubmit,
        formState: {isValid, errors},
        watch,
    } = useForm<{selectedStatus: string; selectedField: string; files: CustomFile[] | null}>({
        defaultValues: {selectedField: '', selectedStatus: '', files: null},
    });

    const values = watch();

    const subHandler = ({selectedStatus, selectedField}: {selectedStatus: string; selectedField: string}) => {
        if (values.files) {
            onUpload(values.files[0], selectedStatus, selectedField);
            if (onFinalUpload) onFinalUpload();
            setValue('files', null);
        }
    };

    return (
        <>
            <AppDialog
                fullWidth
                sx={{p: 3}}
                open={open}
                title={title}
                onCloseDialog={onClose}
                children={
                    <form onSubmit={handleSubmit(subHandler)}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <AppControlledAutoComplete
                                        key={'selectedStatus'}
                                        control={control}
                                        required
                                        options={statusOptions}
                                        shouldReturnValueOnly
                                        label={'Status'}
                                        name="selectedStatus"
                                    />
                                </Stack>
                            </Grid>
                            {fieldsTitles && fieldsTitles.length > 0 && (
                                <Grid item xs={12}>
                                    <Stack spacing={1}>
                                        <AppControlledAutoComplete
                                            key={'selectedField'}
                                            control={control}
                                            options={fieldsTitles}
                                            shouldReturnValueOnly
                                            label={'Field'}
                                            name="selectedField"
                                        />
                                    </Stack>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="file-upload">Attachment</InputLabel>
                                    <SingleFileUpload
                                        maxHeight={230}
                                        setFieldValue={(field, value) =>
                                            setValue(field as 'selectedStatus' | 'selectedField' | 'files', value)
                                        }
                                        file={values.files}
                                        error={!!errors.files}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} pb={2} justifyContent="flex-end">
                                    <Button color="error" variant="outlined" onClick={onClose}>
                                        Cancel
                                    </Button>
                                    <AppButton
                                        isAnimated={!!values.files}
                                        type="submit"
                                        variant="contained"
                                        disabled={!values.files || !isValid}
                                    >
                                        Upload
                                    </AppButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                }
            />
        </>
    );
};

export default AppUploadExcelCsv;
